/**
 * DEV-3694: a release names every product its stores carry, not only the base planogram's.
 *
 * Studio infers the CDT from the base planogram, so a product the stores range but the base
 * does not show was never in the release. Stores then seated it by section alone, with no
 * sub-brand, benefit tier or size, and it shipped as a catch-all block of its brand, placed
 * last. Here each such product joins the CDT group its own attribute values name, tier by tier,
 * read off the base product it shares the most of them with (its closest match):
 *
 * - every value in the release: it joins that group, right after the group's base products;
 * - a value the release does not have: it opens a group of its own, labelled the way the level
 *   labels its values, numbered after its parent's groups (never before them);
 * - a product whose brand has no product on the base is left out and reported.
 *
 * Base products keep their values, priorities and relative order, so a store whose products are
 * all on the base gets the same release as before.
 */
import { COMPOUND_JOIN, sanitizeCdtValue, splitCompoundValue } from "./collapse4";
import { composeLabel, resolveAttrValueOver } from "./finalize";
import { NONE_VALUE } from "./levelInference";
import type { RuleSet, RuleSkuFacts } from "./storeRules";
import {
  PATH_SEPARATOR,
  effectiveAttrs,
  isPlaceholderValue,
  levelsForScope,
  type FinalizeResult,
  type SkuAssignment,
  type StudioDraft,
  type StudioItem,
} from "./studioTypes";

/** A category product, as the product-attributes response gives it. */
export interface StoreRangeProduct {
  productCode: string;
  name: string;
  attrs: Readonly<Record<string, string | null>>;
}

export interface PlacedStoreProduct {
  productCode: string;
  name: string;
  /** The base product it was placed next to. */
  nextTo: { productCode: string; name: string };
  /** True when every value it carries is one the release already has. */
  joinedGroup: boolean;
  path: readonly string[];
}

export interface StoreRangeCoverage {
  placed: readonly PlacedStoreProduct[];
  /** Products no base product shares a brand with: not in the release. */
  unplaced: readonly StoreRangeProduct[];
}

/** The products of the category's attributes response the base does not carry. */
export const storeRangeProductsOf = (
  attributeOverlay: Readonly<Record<string, Readonly<Record<string, string | null>>>> | undefined,
  nameOverlay: Readonly<Record<string, string | null>> | undefined,
  items: readonly StudioItem[],
): StoreRangeProduct[] => {
  const onBase = new Set(items.flatMap((item) => [item.skuId, item.upc]).filter(Boolean));
  return Object.entries(attributeOverlay ?? {})
    .filter(([productCode]) => !onBase.has(productCode))
    .map(([productCode, attrs]) => ({
      productCode,
      name: nameOverlay?.[productCode] ?? productCode,
      attrs,
    }));
};

const BRAND_ATTR = "brand";
const valueOf = (attrs: Readonly<Record<string, unknown>>, attr: string): string =>
  resolveAttrValueOver(attrs, attr);
const magnitudeOf = (value: string): number | null => {
  if (value === NONE_VALUE || isPlaceholderValue(value)) return null;
  const match = value.match(/[\d.]+/);
  return match ? parseFloat(match[0]) : null;
};

/** The product as an item, for the draft's level bands (a raw size read as its tier). */
const asItem = (product: StoreRangeProduct): StudioItem => ({
  skuId: product.productCode,
  upc: "",
  name: product.name,
  run: 0,
  facingWidthCm: 0,
  heightCm: 0,
  facings: 0,
  attrs: product.attrs,
  placements: [],
});

/**
 * The base product each store-range product stands next to: the same brand, then the most
 * leading levels of the draft with the same values (sub-brand, then benefit tier, then size),
 * then the nearest size, then the first on the base.
 */
export const closestBaseMatches = (
  products: readonly StoreRangeProduct[],
  items: readonly StudioItem[],
  draft: StudioDraft,
): Map<string, StudioItem | null> => {
  const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, [])
    .slice(1)
    .map((level) => level.attr);
  const sizeAttr = draft.quantityAttribute ?? levels[levels.length - 1];
  const attrsOf = new Map(items.map((item) => [item.skuId, effectiveAttrs(item, draft)]));
  const matches = new Map<string, StudioItem | null>();
  for (const product of products) {
    const own = effectiveAttrs(asItem(product), draft);
    const brand = valueOf(own, BRAND_ATTR);
    const pool = items.filter(
      (item) => brand !== NONE_VALUE && valueOf(attrsOf.get(item.skuId)!, BRAND_ATTR) === brand,
    );
    const leading = (item: StudioItem): number => {
      const theirs = attrsOf.get(item.skuId)!;
      let count = 0;
      while (
        count < levels.length &&
        valueOf(theirs, levels[count]) === valueOf(own, levels[count])
      )
        count += 1;
      return count;
    };
    const ownSize = sizeAttr ? magnitudeOf(valueOf(own, sizeAttr)) : null;
    const sizeGap = (item: StudioItem): number => {
      const theirs = sizeAttr ? magnitudeOf(valueOf(attrsOf.get(item.skuId)!, sizeAttr)) : null;
      return ownSize === null || theirs === null ? Number.MAX_VALUE : Math.abs(ownSize - theirs);
    };
    let best: StudioItem | null = null;
    for (const item of pool) {
      if (
        best === null ||
        leading(item) > leading(best) ||
        (leading(item) === leading(best) && sizeGap(item) < sizeGap(best))
      ) {
        best = item;
      }
    }
    matches.set(product.productCode, best);
  }
  return matches;
};

/** A cut attribute ("pack_size >= 6") and the plain ones, read off the product's own values. */
const ownFacts = (sibling: RuleSkuFacts, product: StoreRangeProduct): Record<string, string> => {
  const attrs: Record<string, string> = {};
  for (const [attr, value] of Object.entries(sibling.attrs)) {
    if (attr.startsWith("__")) continue;
    const cut = attr.indexOf(" >= ");
    const base = cut >= 0 ? attr.slice(0, cut) : attr;
    attrs[attr] = base in product.attrs ? valueOf(product.attrs, attr) : value;
  }
  return attrs;
};

/**
 * The release with every placeable store-range product added: its assignments, the contract's
 * values and value order, and the measured store rules' facts and strata. `compressedStoreRules`
 * then numbers the walk from the assignments, each one after its group's base products.
 */
export const coverStoreRange = (
  result: FinalizeResult,
  measured: RuleSet,
  items: readonly StudioItem[],
  draft: StudioDraft,
  products: readonly StoreRangeProduct[],
): { result: FinalizeResult; measured: RuleSet; coverage: StoreRangeCoverage } => {
  if (products.length === 0) {
    return { result, measured, coverage: { placed: [], unplaced: [] } };
  }
  const levels = result.contract.levels;
  const factsOf = new Map(measured.skus.map((facts) => [facts.skuId, facts]));
  const itemOf = new Map(items.map((item) => [item.skuId, item]));
  const matches = closestBaseMatches(products, items, draft);

  const assignments: SkuAssignment[] = [...result.assignments];
  const itemValues: Record<string, readonly string[]> = { ...result.contract.itemValues };
  const valueOrder: Record<string, string[]> = Object.fromEntries(
    Object.entries(result.contract.valueOrder).map(([attr, order]) => [attr, [...order]]),
  );
  const extraFacts: RuleSkuFacts[] = [];
  const placed: PlacedStoreProduct[] = [];
  const unplaced: StoreRangeProduct[] = [];

  const pathOf = (assignment: SkuAssignment) => [
    assignment.cdt0,
    assignment.cdt1,
    assignment.cdt2,
    assignment.cdt3,
  ];
  const priorityKeys = ["cdt1Priority", "cdt2Priority", "cdt3Priority"] as const;
  const samePrefix = (a: readonly string[], b: readonly string[], depth: number) =>
    a.slice(0, depth).join(PATH_SEPARATOR) === b.slice(0, depth).join(PATH_SEPARATOR);

  const baseAssignments = result.assignments;
  const candidateAttrs = [
    ...new Set([
      ...levels.slice(1).flatMap((level) => level.sourceAttrs),
      ...levelsForScope(draft.spine, draft.scopedLevelOrder, []).map((level) => level.attr),
    ]),
  ];
  const attrsOf = new Map(items.map((item) => [item.skuId, effectiveAttrs(item, draft)]));
  /** The attribute a level reads in one group of the base: the first whose value decides each
   *  product's label there (the release reads the tiers off the base, so a brand's second level
   *  may be its sizes while another brand's is its benefit tier). Null where the level holds a
   *  single value in that group. */
  const readingOf = (
    scope: readonly SkuAssignment[],
    tier: number,
  ): { attr: string; labelOf: Map<string, string> } | null => {
    const labels = new Set(scope.map((entry) => pathOf(entry)[tier]));
    if (labels.size <= 1) return null;
    for (const attr of candidateAttrs) {
      const labelOf = new Map<string, string>();
      const decides = scope.every((entry) => {
        const value = valueOf(attrsOf.get(entry.skuId) ?? {}, attr);
        const label = pathOf(entry)[tier];
        if ((labelOf.get(value) ?? label) !== label) return false;
        labelOf.set(value, label);
        return true;
      });
      if (decides) return { attr, labelOf };
    }
    return null;
  };

  for (const product of products) {
    const match = matches.get(product.productCode) ?? null;
    const sibling = match ? assignments.find((entry) => entry.skuId === match.skuId) : undefined;
    const siblingFacts = match ? factsOf.get(match.skuId) : undefined;
    if (!match || !sibling || !siblingFacts) {
      unplaced.push(product);
      continue;
    }
    const own = effectiveAttrs(asItem(product), draft);
    const siblingPath = pathOf(sibling);
    /** The level's own label for the product, read off the attributes the level ships. */
    const ownLabel = (tier: number, fallback: string) =>
      composeLabel(
        (levels[tier]?.sourceAttrs ?? []).map((attr) => valueOf(own, attr)),
        fallback,
      );
    // cdt0 first: a brand carved into size rows ("PT-V small" / "PT-V large") seats the product
    // in the band its own size belongs to, the nearest size where the base has not this one.
    const section = siblingFacts.section;
    const sectionScope = baseAssignments.filter(
      (entry) => factsOf.get(entry.skuId)?.section === section,
    );
    const cdt0Read = readingOf(sectionScope, 0);
    let cdt0 = siblingPath[0];
    if (cdt0Read) {
      const value = valueOf(own, cdt0Read.attr);
      const magnitude = magnitudeOf(value);
      const nearest = [...cdt0Read.labelOf]
        .map(([key, label]) => ({
          label,
          gap: Math.abs((magnitudeOf(key) ?? NaN) - (magnitude ?? NaN)),
        }))
        .filter((entry) => Number.isFinite(entry.gap))
        .sort((x, y) => x.gap - y.gap)[0];
      cdt0 = cdt0Read.labelOf.get(value) ?? nearest?.label ?? cdt0;
    }
    // Then tier by tier, inside the group chosen so far: the base's group whose products carry
    // the product's value of the attribute that level reads in this group, else its own group.
    const path: string[] = [cdt0];
    let joined = true;
    for (let tier = 1; tier < 4; tier += 1) {
      const scope = baseAssignments.filter((entry) =>
        samePrefix(pathOf(entry), joined ? path : siblingPath, tier),
      );
      const read = readingOf(scope, tier);
      if (read === null) {
        // One value in this group: blank (the level is not read here) or the product's own.
        const only = scope[0] ? pathOf(scope[0])[tier] : siblingPath[tier];
        const theirs = scope[0] ? effectiveAttrs(itemOf.get(scope[0].skuId)!, draft) : own;
        const same = (levels[tier]?.sourceAttrs ?? []).every(
          (attr) => valueOf(own, attr) === valueOf(theirs, attr),
        );
        if (only === "" || (joined && same)) {
          path.push(only);
          continue;
        }
        joined = false;
        path.push(ownLabel(tier, only));
        continue;
      }
      const value = valueOf(own, read.attr);
      const label = joined ? read.labelOf.get(value) : undefined;
      if (label !== undefined) {
        path.push(label);
        continue;
      }
      joined = false;
      // A cdt1 that joins the sub-brand with the value it reads ("Signature Cloth : Full sheet
      // Double", collapse4's cdt1 join) names the new group the same way, with the product's own
      // sub-brand in front.
      const joinsValue = [...read.labelOf].every(([key, known]) => {
        const parts = splitCompoundValue(known);
        return parts.length > 1 && parts[parts.length - 1] === sanitizeCdtValue(key);
      });
      path.push(
        joinsValue
          ? [ownLabel(tier, siblingPath[tier]), sanitizeCdtValue(value)].join(COMPOUND_JOIN)
          : composeLabel([value], siblingPath[tier]),
      );
    }

    // Priorities: an existing group keeps its own; a new one comes after its parent's groups.
    const priorities = [0, 0, 0];
    for (let tier = 1; tier < 4; tier += 1) {
      const key = priorityKeys[tier - 1];
      const same = assignments.find((entry) => samePrefix(pathOf(entry), path, tier + 1));
      if (same) {
        priorities[tier - 1] = same[key];
        continue;
      }
      const siblings = assignments.filter((entry) => samePrefix(pathOf(entry), path, tier));
      priorities[tier - 1] = Math.max(0, ...siblings.map((entry) => entry[key])) + 1;
    }
    const assignment: SkuAssignment = {
      skuId: product.productCode,
      upc: "",
      name: product.name,
      cdt0: path[0],
      cdt1: path[1],
      cdt2: path[2],
      cdt3: path[3],
      cdt1Priority: priorities[0],
      cdt2Priority: priorities[1],
      cdt3Priority: priorities[2],
    };
    // In the assignments right after the deepest group it shares with the base, so the walk
    // `compressedStoreRules` sorts (stably) seats it after that group's products.
    let at = -1;
    for (let depth = 4; depth >= 1 && at < 0; depth -= 1) {
      for (let index = assignments.length - 1; index >= 0; index -= 1) {
        if (samePrefix(pathOf(assignments[index]), path, depth)) {
          at = index;
          break;
        }
      }
    }
    assignments.splice(at + 1, 0, assignment);
    // Named to the merchant by the base product it now stands after.
    let neighbour: SkuAssignment = sibling;
    for (let depth = 4; depth >= 1; depth -= 1) {
      const shared = baseAssignments.filter((entry) => samePrefix(pathOf(entry), path, depth));
      if (shared.length > 0) {
        const rankOf = (entry: SkuAssignment) => factsOf.get(entry.skuId)?.walkRank ?? -1;
        neighbour = shared.reduce((last, entry) => (rankOf(entry) > rankOf(last) ? entry : last));
        break;
      }
    }
    itemValues[product.productCode] = path;
    levels.forEach((level, tier) => {
      const order = valueOrder[level.attr];
      if (order && !order.includes(path[tier])) order.push(path[tier]);
    });
    const template = factsOf.get(neighbour.skuId) ?? siblingFacts;
    extraFacts.push({
      ...template,
      skuId: product.productCode,
      baseFacings: 0,
      cdt1: path[1],
      attrs: {
        ...ownFacts(template, product),
        __cdt1: path[1],
        __cdt2: path[2],
        __cdt3: path[3],
      },
    });
    placed.push({
      productCode: product.productCode,
      name: product.name,
      nextTo: { productCode: neighbour.skuId, name: neighbour.name },
      joinedGroup: joined,
      path,
    });
  }

  const stratumOf = new Map(extraFacts.map((facts) => [facts.skuId, facts.cdt0]));
  return {
    result: {
      ...result,
      assignments,
      contract: { ...result.contract, itemValues, valueOrder },
    },
    measured: {
      ...measured,
      skus: [...measured.skus, ...extraFacts],
      strata: measured.strata.map((stratum) => {
        const joining = extraFacts
          .filter((facts) => stratumOf.get(facts.skuId) === stratum.cdt0)
          .map((facts) => facts.skuId);
        return joining.length > 0
          ? { ...stratum, skuIds: [...stratum.skuIds, ...joining] }
          : stratum;
      }),
    },
    coverage: { placed, unplaced },
  };
};
